<template>
    <div class="h-full w-full flex flex-col bg-gradient-to-br from-gray-100 to-blue-50">
        <div class="h-full container mx-auto">
            <div class="px-4 sm:px-8 flex flex-col h-full w-full py-4 sm:py-8">
                <div class="w-full bg-white rounded-2xl border shadow-lg px-6 md:px-8 py-4 flex flex-col">
                    <div class="flex items-center text-sm font-light">
                        <router-link to="/dashboard" class="opacity-50 hover:opacity-100">Dashboard</router-link>
                        <span class="mx-2">/</span>
                        <span>Highlights</span>
                    </div>
                    <div class="font-bold">Highlights</div>
                </div>

                <div class="mt-8 flex flex-row-reverse flex-wrap">
                    <div class="w-full lg:w-1/4 lg:pl-4" v-if="recentUpdates.length > 0">
                        <div
                            class="h-full w-full flex flex-col bg-white rounded-2xl border shadow-lg py-6 md:py-8 px-4">
                            <h1 class="text-base md:text-lg font-semibold mb-2">Recent Updates</h1>
                            <transition-group name="fade" tag="ul" class="highlights-list">
                                <div v-for="(log, index) in recentUpdates" :key="index"
                                    class="overflow-x-hidden border mt-2 rounded-2xl px-4 py-2 cursor-pointer hover:bg-gray-100">
                                    <div class="flex items-center w-full overflow-x-hidden">
                                        <div class="flex-1">
                                            <p class="text-sm md:text-base font-medium text-gray-800">{{ log.activity }}
                                            </p>
                                            <p class="text-xs text-gray-600 truncate-2">{{ getLogShortDescription(log)
                                                }}
                                            </p>
                                            <span class="text-xs text-gray-500">{{ timeAgo(log.created_at) }}</span>
                                        </div>
                                    </div>
                                </div>
                            </transition-group>
                        </div>
                    </div>
                    <div :class="recentUpdates.length > 0 ? 'w-full lg:w-3/4' : 'w-full'" class="lg:pr-4 mt-8 lg:mt-0">
                        <section class="h-full w-full flex flex-col bg-white rounded-2xl border shadow-lg p-6 md:p-8">
                            <h1 class="text-base md:text-lg font-semibold mb-4">Highlights</h1>
                            <div class="flex justify-start items-center flex-wrap w-full">
                                <div
                                    class="w-full sm:w-auto mr-0 sm:mr-8 mb-4 border rounded-lg px-4 h-8 flex items-center">
                                    <div class="text-sm mr-2">User:</div>
                                    <select v-model="filterByRole" class="outline-none text-sm flex-grow">
                                        <option value="">All</option>
                                        <option v-for="(item, index) in filteredRoles" :key="index" :value="item.name">
                                            {{ item.display_name }}</option>
                                    </select>
                                </div>
                                <div
                                    class="w-full sm:w-auto mr-0 sm:mr-8 mb-4 border rounded-lg px-4 h-8 flex items-center">
                                    <div class="text-sm mr-2">Date Range:</div>
                                    <div>
                                        <date-picker v-model:value="filterByDateRange" format="YYYY-MM-DD" type="date"
                                            range value-type="format" placeholder="Select Date Range"></date-picker>
                                    </div>
                                </div>
                                <div
                                    class="w-full sm:w-auto mr-0 sm:mr-8 mb-4 border rounded-lg px-4 h-8 flex items-center">
                                    <div class="text-sm mr-2">Activity:</div>
                                    <select v-model="filterByActivity" class="outline-none text-sm flex-grow">
                                        <option value="">All</option>
                                        <option v-for="(item, index) in filteredActivities" :key="index" :value="item">
                                            {{ item }}</option>
                                    </select>
                                </div>
                                <div
                                    class="w-full sm:w-auto mr-0 sm:mr-8 mb-4 border rounded-lg px-4 h-8 flex items-center">
                                    <div class="text-sm mr-2">Entity:</div>
                                    <select v-model="filterByEntity" class="outline-none text-sm flex-grow">
                                        <option value="">All</option>
                                        <option :value="item.entity" v-for="(item, index) in filteredEntities"
                                            :key="index">{{
                                                getEntityName(item.entity) }}</option>
                                    </select>
                                </div>
                                <div
                                    class="w-full sm:w-auto mr-0 sm:mr-8 mb-4 border rounded-lg px-4 h-8 flex items-center">
                                    <div class="text-sm mr-2">Campus:</div>
                                    <select v-model="filterByCampus" class="outline-none text-sm flex-grow">
                                        <option value="">All</option>
                                        <option :value="item.campus_code" v-for="(item, index) in filteredCampuses"
                                            :key="index">{{ item.campus_code }}</option>
                                    </select>
                                </div>
                            </div>

                            <div class="flex justify-start">
                                <div @click.stop="clearAllFilter()"
                                    class="px-3 py-1 text-sm border border-red-500 bg-red-500 hover:bg-white text-white hover:text-red-500 rounded-lg cursor-pointer">
                                    Clear All Filter
                                </div>
                            </div>

                            <div class="flex flex-col" v-if="$apollo.queries.highlights.loading">
                                <table-shimmer
                                    :titleHeaders="['#', 'Timestamp', 'User', 'Activity', 'Details', 'IP Address']"
                                    :rows="first"></table-shimmer>
                            </div>
                            <div v-else-if="!$apollo.queries.highlights.loading && !hasItems"
                                class="h-full flex justify-center items-center">
                                <h1 class="text-center text-xl font-medium">No results found!</h1>
                            </div>
                            <div v-else class="flex flex-col">
                                <div class="flex flex-col overflow-x-auto">
                                    <div class="inline-block min-w-full py-2">
                                        <div class="overflow-x-auto">
                                            <table class="min-w-full text-left text-sm font-light">
                                                <thead class="border-b font-medium dark:border-neutral-500">
                                                    <tr>
                                                        <th scope="col" class="px-6 py-2 text-gray-400 w-8">#</th>
                                                        <th scope="col" class="px-6 py-2 text-gray-400">Timestamp
                                                        </th>
                                                        <th scope="col" class="px-6 py-2 text-gray-400">User</th>
                                                        <th scope="col" class="px-6 py-2 text-gray-400">Activity
                                                        </th>
                                                        <th scope="col" class="px-6 py-2 text-gray-400">Details</th>
                                                        <th scope="col" class="px-6 py-2 text-gray-400">IP Address
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="(item, index) in items" :key="index"
                                                        class="border-b border-dashed dark:border-neutral-500">
                                                        <td class="whitespace-nowrap px-6 py-2 font-medium">{{
                                                            paginatorInfo.firstItem + index }}</td>
                                                        <td class="px-6 py-2">{{ item.created_at }}</td>
                                                        <td v-if="item.user"
                                                            @click.stop="goToUserProfile(item.user.token)"
                                                            :title="`Click to view ${item.user.name}'s profile`"
                                                            class="px-6 py-2 cursor-pointer hover:text-blue-600 transition-colors">
                                                            {{ item.user.name }}</td>
                                                        <td v-else class="px-6 py-2">System</td>
                                                        <td class="px-6 py-2">{{ item.activity }}</td>
                                                        <td class="px-6 py-2">
                                                            <div class="w-48 truncate-3">{{ item.details }}</div>
                                                        </td>
                                                        <td class="px-6 py-2">{{ item.ip_address }}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-4">
                                    <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import gql from 'graphql-tag';
import Pagination from "../../components/shared/pagination.vue";
import TableShimmer from '../../components/shared/TableShimmer.vue';
export default {
    name: "highlights-index",
    components: {
        Pagination,
        TableShimmer,
    },
    data() {
        return {
            highlights: [],
            first: 10,
            page: 1,
            recentUpdates: [],
            systemTime: null,
            filterByRole: "",
            filterByDateRange: [],
            filterByActivity: "",
            filterByEntity: "",
            filterByCampus: "",
            roles: [],
            activities: [],
            entities: [],
            campusList: [],
            intervalId: null,
        };
    },
    created() {
        this.getCampusList();
        this.fetchRecentUpdates();
        // Auto-refresh updates every 10 seconds
        this.intervalId = setInterval(async () => {
            await this.fetchRecentUpdates();
        }, 10000);
    },
    beforeUnmount() {
        if (this.intervalId) clearInterval(this.intervalId);
    },
    watch: {
        highlights(newVal) {
            if (newVal.error) {
                if (newVal.error_code == 403) {
                    this.$router.replace('/no-permission');
                } else if (newVal.error_code == 401) {
                    this.$router.replace('/');
                } else {
                    alert(newVal.message);
                    this.$router.replace('/');
                }
            }
        },
        filterByRole() {
            this.page = 1;
        },
        filterByDateRange: {
            handler() {
                this.page = 1;
            },
            deep: true,
        },
        filterByActivity() {
            this.page = 1;
        },
        filterByEntity() {
            this.filterByCampus = "";
            this.page = 1;
        },
        filterByCampus() {
            this.page = 1;
        },
    },
    computed: {
        items() {
            return this.highlights.data;
        },
        hasItems() {
            if (this.items && this.items.length > 0) {
                return true;
            } else {
                return false;
            }
        },
        paginatorInfo() {
            return this.highlights.paginatorInfo;
        },
        role() {
            return this.$store.getters.role;
        },
        filteredRoles() {
            return this.roles.data;
        },
        filteredActivities() {
            return this.activities;
        },
        filteredEntities() {
            return this.entities.data;
        },
        filteredCampuses() {
            if (this.filterByEntity) {
                return this.campusList.filter(
                    (campus) => campus.entity === this.filterByEntity
                );
            } else {
                return this.campusList;
            }
        },
    },
    apollo: {
        highlights() {
            return {
                query: gql`query GetAllHighlights($first: Int!, $page: Int!, $role: String, $start_date: Date, $end_date: Date, $activity: String, $entity: String, $campus: String) {
                    highlights(first: $first, page: $page, role: $role, start_date: $start_date, end_date: $end_date, activity: $activity, entity: $entity, campus: $campus) {
                        error
                        error_code
                        message
                        data {
                            id
                            user_id
                            module
                            action
                            activity
                            details
                            level
                            ip_address
                            browser_info
                            created_at
                            updated_at
                            user {
                                id
                                token
                                name
                            }
                        }
                        paginatorInfo {
                            count
                            currentPage
                            firstItem
                            hasMorePages
                            lastItem
                            lastPage
                            perPage
                            total
                        }
                    }
                }`,
                variables() {
                    return {
                        first: this.first,
                        page: this.page,
                        role: this.filterByRole,
                        start_date: this.filterByDateRange[0],
                        end_date: this.filterByDateRange[1],
                        activity: this.filterByActivity,
                        entity: this.filterByEntity,
                        campus: this.filterByCampus,
                    }
                },
                fetchPolicy: 'cache-and-network'
            }
        },
        roles() {
            return {
                query: gql`query GetAllRoles($first: Int!, $page: Int!) {
                    roles(first: $first, page: $page) {
                        data {
                            id
                            name
                            display_name
                        }
                        paginatorInfo {
                            count
                            currentPage
                            firstItem
                            hasMorePages
                            lastItem
                            lastPage
                            perPage
                            total
                        }
                    }
                }`,
                variables() {
                    return {
                        first: 100,
                        page: 1,
                    }
                },
            }
        },
        activities() {
            return {
                query: gql`query GetAllActivities {
                    activities
                }`,
            }
        },
        entities() {
            return {
                query: gql`
                    query GetEntityList {
                        entities {
                        data {
                            entity
                        }
                        }
                    }
                `,
                fetchPolicy: "cache-and-network",
            };
        },
    },
    methods: {
        goToUserProfile(token) {
            this.$router.push(`/profile/${token}`);
        },
        setPage(page) {
            this.page = page;
        },
        async fetchRecentUpdates() {
            try {

                if (!this.$apollo) {
                    return;
                }

                const { data } = await this.$apollo.query({
                    query: gql`
                        query FetchRecentUpdates {
                        fetchRecentUpdates {
                            data {
                                id
                                user_id
                                module
                                action
                                activity
                                details
                                level
                                ip_address
                                browser_info
                                created_at
                                updated_at
                                user {
                                    name
                                }
                            }
                            system_time
                        }
                    }`,
                    fetchPolicy: "network-only",
                });
                this.recentUpdates = data.fetchRecentUpdates.data;
                this.systemTime = data.fetchRecentUpdates.system_time;
            } catch (error) {
                console.error("Error fetching updates:", error);
            }
        },
        timeAgo(timestamp) {
            const now = new Date(this.systemTime);
            const past = new Date(timestamp);
            const diffInSeconds = Math.floor((now - past) / 1000);

            if (diffInSeconds < 60) {
                return `${diffInSeconds} seconds ago`;
            } else if (diffInSeconds < 3600) {
                return `${Math.floor(diffInSeconds / 60)} minutes ago`;
            } else if (diffInSeconds < 86400) {
                return `${Math.floor(diffInSeconds / 3600)} hours ago`;
            } else {
                return `${Math.floor(diffInSeconds / 86400)} days ago`;
            }
        },
        getLogShortDescription(log) {
            if (log.user) {
                return `${log.user.name} - ${log.details}`;
            } else {
                return `${log.details}`;
            }
        },
        clearAllFilter() {
            this.filterByRole = ""
            this.filterByDateRange = [];
            this.filterByActivity = "";
            this.filterByEntity = "";
            this.filterByCampus = "";
        },
        getEntityName(entity) {
            return entity.replace(/ATHS/g, "ATS");
        },
        async getCampusList() {
            try {
                const { data } = await this.$apollo.query({
                    query: gql`
            query GetCampusList($student_status: String, $entity: String) {
              getCampusList(student_status: $student_status, entity: $entity) {
                entity
                campus_code
              }
            }
          `,
                    variables: {
                        student_status: '',
                        entity: '',
                    },
                    fetchPolicy: "network-only",
                });
                this.campusList = data.getCampusList;
            } catch (error) {
                console.log(error);
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.truncate-3 {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
}

.truncate-2 {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}

/* Fade animation */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>

<style lang="scss">
.mx-datepicker {
    width: 240px !important;
}

.mx-input-wrapper {
    .mx-input {
        height: auto !important;
        border: none;
        padding: 5px;
    }
}
</style>